import type { IPollData, PollType } from '@/models/Poll'

export interface IPollOptionStats {
  percentage: number | null
  canViewVoters: boolean
  hasVoters: boolean
}

export interface IPollSnapshot {
  poll: IPollData
  data: IPollData
}

type PollErrorLike = {
  response?: {
    data?: {
      type?: string
    }
  }
  type?: string
}

export function clonePoll(poll: IPollData): IPollData {
  return JSON.parse(JSON.stringify(poll))
}

export function createPollSnapshots(polls: IPollData[]): IPollSnapshot[] {
  return polls.map(poll => ({
    poll,
    data: clonePoll(poll),
  }))
}

export function restorePollSnapshots(snapshots: IPollSnapshot[]): void {
  snapshots.forEach(({ poll, data }) => {
    Object.assign(poll, data)
  })
}

export function applyOptimisticPollVote(poll: IPollData, optionId: string, pollType: PollType): void {
  const hadVotesBefore = poll.userVote.length > 0

  if (pollType === 'SINGLE_SELECT') {
    const previousOptionId = poll.userVote[0]
    if (previousOptionId === optionId) return

    poll.userVote = [optionId]
    if (poll.optionVoteCount) {
      if (previousOptionId) {
        poll.optionVoteCount[previousOptionId] = Math.max(0, (poll.optionVoteCount[previousOptionId] || 0) - 1)
      }
      poll.optionVoteCount[optionId] = (poll.optionVoteCount[optionId] || 0) + 1
    }
    if (typeof poll.voterCount === 'number' && !hadVotesBefore) {
      poll.voterCount += 1
    }
    return
  }

  if (poll.userVote.includes(optionId)) return

  poll.userVote.push(optionId)
  if (poll.optionVoteCount) {
    poll.optionVoteCount[optionId] = (poll.optionVoteCount[optionId] || 0) + 1
  }
  if (typeof poll.voterCount === 'number' && !hadVotesBefore) {
    poll.voterCount += 1
  }
}

export function applyOptimisticPollDeselect(poll: IPollData, optionId: string): void {
  if (!poll.userVote.includes(optionId)) return

  const previousVotesCount = poll.userVote.length
  poll.userVote = poll.userVote.filter(voteId => voteId !== optionId)

  if (poll.optionVoteCount) {
    poll.optionVoteCount[optionId] = Math.max(0, (poll.optionVoteCount[optionId] || 0) - 1)
  }

  if (typeof poll.voterCount === 'number' && previousVotesCount > 0 && poll.userVote.length === 0) {
    poll.voterCount = Math.max(0, poll.voterCount - 1)
  }
}

export function getPollOptionStatsMap(poll: IPollData): Record<string, IPollOptionStats> {
  const optionVoteCount = poll.optionVoteCount
  if (!optionVoteCount) {
    return Object.fromEntries(poll.options.map(option => [option._id, { percentage: null, canViewVoters: false, hasVoters: false }]))
  }

  const totalVotes = poll.options.reduce((total, option) => {
    const count = optionVoteCount[option._id] || 0
    return total + count
  }, 0)

  return Object.fromEntries(
    poll.options.map(option => {
      const count = optionVoteCount[option._id] || 0
      const percentage = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0
      return [
        option._id,
        {
          percentage,
          canViewVoters: count > 0,
          hasVoters: count > 0,
        },
      ]
    })
  )
}

export function mapPollErrorMessage(error: unknown, t: (key: string) => string): string {
  const typedError = error as PollErrorLike
  const errorType = typedError?.response?.data?.type || typedError?.type
  switch (errorType) {
    case 'POLL_VOTE_POLL_ENDED':
      return t('post.poll.errors.pollEnded')
    case 'POLL_VOTE_NOT_GROUP_MEMBER':
      return t('post.poll.errors.notGroupMember')
    case 'POLL_VOTE_UNKNOWN_OPTION':
      return t('post.poll.errors.unknownOption')
    default:
      return t('post.poll.errors.generic')
  }
}
